import React, { useMemo } from "react";
import s from "./style.module.css";
import Vizualisation from "../../../../components/Vizualisation";
import useVizualisation from "../../../../helpers/useVizualisation";
import { a, useTransition } from "react-spring";
import { scaleLinear } from "d3-scale";
import countries from "../../data/countries.json";
import ixp from "../../data/ixp.json";
import data_centers from "../../data/data_centers.json";
import cloud_on_ramps from "../../data/cloud_on_ramps.json";
import { wordwrap } from "d3-jetpack";

const margin = {
  top: 30,
  right: 50,
  bottom: 30,
  left: 50,
};

const height = 700;
const R = 18;
const countryMargin = 5;
const lowColumns = 6;
const highColumns = lowColumns;

const sceneProps = ["ixp", "data_center", "cloud_on_ramp"];

const CountryCircles = ({ items, index }) => {
  const c = scaleLinear()
    .domain([0, 1])
    .range(["#CBD8D9", "#F2C118"])
    .clamp(true);

  const countryItems = items
    .filter((d) => d[sceneProps[index]] >= 1)
    .map((d, i) => ({
      ...d,
      xy: [
        (i % lowColumns) * (2 * R + countryMargin),
        2 * R + Math.floor(i / lowColumns) * (2 * R + countryMargin),
      ],
      fill: c(d[`${sceneProps[index]}_rpm`]),
      fillOpacity: d[`${sceneProps[index]}_rpm`],
      strokeFill: "black",
    }))
    .concat(
      items
        .filter((d) => d[sceneProps[index]] === 0)
        .map((d, i) => ({
          ...d,
          xy: [
            (i % lowColumns) * (2 * R + countryMargin),
            485 -
              Math.ceil(
                items.filter((d) => d[sceneProps[index]] === 0).length /
                  lowColumns
              ) *
                (2 * R + countryMargin) +
              2 * R +
              Math.floor(i / lowColumns) * (2 * R + countryMargin),
          ],
          fill: "white",
          fillOpacity: 1,
          strokeFill: "#999",
        }))
    );

  const transitions = useTransition(countryItems, (item) => item.code, {
    from: ({ xy, fill }) => ({ xy, fill, opacity: 1 }),
    enter: ({ xy, fill }) => ({ xy, fill, opacity: 1 }),
    update: ({ xy, fill, strokeFill }) => ({
      xy,
      fill,
      strokeFill,
      opacity: 1,
    }),
    leave: { opacity: 0 },
    config: { mass: 5, tension: 500, friction: 100 },
  });

  return (
    <g>
      {transitions.map(
        ({ item, props: { xy, fill, strokeFill, opacity, ...rest }, key }) => (
          <a.g
            transform={xy.interpolate((x, y) => `translate(${x}, ${y})`)}
            opacity={opacity}
            key={key}
          >
            <a.circle
              className={s.countryCircle}
              r={R}
              fill={fill}
              style={{ stroke: strokeFill }}
            />
            <a.text fill={strokeFill} className={s.countryLabel}>
              {item.code}
            </a.text>
          </a.g>
        )
      )}
    </g>
  );
};

const Infrastructure = ({
  data,
  title,
  note,
  source,
  index = 0,
  id,
  remark,
  highIncome,
  lowIncome,
  countriesLower,
  countriesCapital,
  without,
  any,
  ofAllLowIncomeCountries,
  ofAllHighIncomeCountries,
  have,
  atLeastOne,
  indicatorLabels,
  indicatorDescriptions,
  ...rest
}) => {
  const { ref, width } = useVizualisation();

  const high2 = countries
    .filter((d) => d.income_group === "High income")
    .map((d) => {
      const findIxp = ixp.find((x) => x.wb_name === d.wb_name);
      const findDataCenter = data_centers.find((x) => x.wb_name === d.wb_name);
      const findCloudOnRamp = cloud_on_ramps.find(
        (x) => x.wb_name === d.wb_name
      );

      return {
        ...d,
        ixp: findIxp ? findIxp.count : null,
        ixp_rpm: findIxp ? findIxp.ratio_per_million : null,
        data_center: findDataCenter ? findDataCenter.count : 0,
        data_center_rpm: findDataCenter ? findDataCenter.ratio_per_million : 0,
        cloud_on_ramp: findCloudOnRamp ? findCloudOnRamp.count : 0,
        cloud_on_ramp_rpm: findCloudOnRamp
          ? findCloudOnRamp.ratio_per_million
          : 0,
      };
    });

  const low2 = countries
    .filter((d) => d.income_group === "Low income")
    .map((d) => {
      const findIxp = ixp.find((x) => x.wb_name === d.wb_name);
      const findDataCenter = data_centers.find((x) => x.wb_name === d.wb_name);
      const findCloudOnRamp = cloud_on_ramps.find(
        (x) => x.wb_name === d.wb_name
      );
      return {
        ...d,
        ixp: findIxp ? findIxp.count : null,
        ixp_rpm: findIxp ? findIxp.ratio_per_million : null,
        data_center: findDataCenter ? findDataCenter.count : 0,
        data_center_rpm: findDataCenter ? findDataCenter.ratio_per_million : 0,
        cloud_on_ramp: findCloudOnRamp ? findCloudOnRamp.count : 0,
        cloud_on_ramp_rpm: findCloudOnRamp
          ? findCloudOnRamp.ratio_per_million
          : 0,
      };
    });

  const labels = ["IXP", "data center", "cloud on-ramp"];

  const w = Math.max(width, 650) - margin.left - margin.right;
  const h = height - margin.top - margin.bottom;

  return (
    <Vizualisation ref={ref} note={note} source={source} size="large">
      <svg viewBox={`0 0 ${Math.max(width, 650)} ${height}`}>
        <defs>
          <linearGradient id="grad1" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop
              offset="0%"
              style={{
                stopColor: "#CBD8D9",
                stopOpacity: 1,
              }}
            />
            <stop
              offset="100%"
              style={{
                stopColor: "#F2C118",
                stopOpacity: 1,
              }}
            />
          </linearGradient>
        </defs>
        <g transform={`translate(${margin.left}, ${margin.top})`}>
          <g className="low-income" transform="translate(0, 50)">
            <text dy={-50}>
              <tspan style={{ fontWeight: "bold" }}>{lowIncome}</tspan>{" "}
              {countriesLower}
            </text>
            <rect
              x={-R - R}
              y={-30}
              width={lowColumns * 2 * R + 2 * R + 2 * R}
              height={h - 70}
              rx={R}
              ry={R}
              stroke="#EE6047"
              fill="none"
              style={{ strokeWidth: 2 }}
            />
            <g
              transform={`translate(${
                -R - R + (lowColumns * 2 * R + 2 * R + 2 * R) / 2
              }, 0)`}
              className={s.topLabel}
            >
              <text
                className={s.topicLabelPercentage}
                style={{ fontWeight: "bold" }}
                dy={-6}
              >
                {Math.round(
                  (100 *
                    low2.filter(
                      (d) =>
                        d[sceneProps[index]] !== null &&
                        d[sceneProps[index]] >= 1
                    ).length) /
                    low2.filter((d) => d[sceneProps[index]] !== null).length
                )}
                %
              </text>
              <text y={12}>
                {/* <tspan
                  className={s.topicLabelPercentage}
                  style={{ fontWeight: "bold" }}
                >
                  {Math.round(
                    (100 *
                      low2.filter(
                        (d) =>
                          d[sceneProps[index]] !== null &&
                          d[sceneProps[index]] >= 1
                      ).length) /
                      low2.filter((d) => d[sceneProps[index]] !== null).length
                  )}
                  %
                </tspan>{" "} */}
                {ofAllLowIncomeCountries}...
              </text>
              <text y={32} className={s.topicLabel}>
                ...{have}{" "}
                <tspan style={{ fontWeight: "bold" }}>
                  {atLeastOne} {labels[index]}
                </tspan>
              </text>
            </g>
            <g transform="translate(0, 30)">
              <CountryCircles
                index={index}
                items={low2
                  .filter((d) => d[sceneProps[index]] !== null)
                  .sort(
                    (a, b) =>
                      b[`${sceneProps[index]}_rpm`] -
                      a[`${sceneProps[index]}_rpm`]
                  )}
              />
            </g>
            <g
              transform={`translate(${0}, ${
                h -
                100 -
                Math.ceil(
                  low2.filter(
                    (d) =>
                      d[sceneProps[index]] !== null &&
                      d[sceneProps[index]] === 0
                  ).length / highColumns
                ) *
                  (2 * R + countryMargin) -
                (R + countryMargin)
              })`}
            >
              <text
                className={s.noInfraLabel}
                x={-R - R + (lowColumns * 2 * R + 2 * R + 2 * R) / 2}
              >
                {countriesCapital}{" "}
                <tspan style={{ fontWeight: "bold" }}>{without}</tspan> {any}{" "}
                {labels[index]}
              </text>
            </g>
            <g
              className="high-income"
              transform={`translate(${w}, 0)`}
              style={{ textAnchor: "end" }}
            >
              <text dy={-50}>
                <tspan style={{ fontWeight: "bold" }}>{highIncome}</tspan>{" "}
                {countriesLower}
              </text>
              <rect
                x={-R - R - highColumns * R * 2}
                y={-30}
                width={highColumns * 2 * R + 2 * R + 2 * R}
                height={h - 70}
                rx={R}
                ry={R}
                stroke="#481F7C"
                fill="none"
                style={{ strokeWidth: 2 }}
              />
              <g
                transform={`translate(${
                  -R -
                  R -
                  highColumns * R * 2 +
                  (highColumns * 2 * R + 2 * R + 2 * R) / 2
                }, 0)`}
                className={s.topLabel}
              >
                <text
                  className={s.topicLabelPercentage}
                  style={{ fontWeight: "bold" }}
                  dy={-6}
                >
                  {Math.round(
                    (100 *
                      high2.filter(
                        (d) =>
                          d[sceneProps[index]] !== null &&
                          d[sceneProps[index]] >= 1
                      ).length) /
                      high2.filter((d) => d[sceneProps[index]] !== null).length
                  )}
                  %
                </text>
                <text y={12}>{ofAllHighIncomeCountries}...</text>
                <text y={32} className={s.topicLabel}>
                  ...{have}{" "}
                  <tspan style={{ fontWeight: "bold" }}>
                    {atLeastOne} {labels[index]}
                  </tspan>
                </text>
              </g>
              <g
                transform={`translate(${
                  -2 * R * highColumns + countryMargin
                }, 30)`}
              >
                <CountryCircles
                  index={index}
                  items={high2
                    .filter((d) => d[sceneProps[index]] !== null)
                    .sort(
                      (a, b) =>
                        b[`${sceneProps[index]}_rpm`] -
                        a[`${sceneProps[index]}_rpm`]
                    )}
                />
              </g>
              <g
                transform={`translate(${-2 * R * highColumns}, ${
                  h -
                  100 -
                  Math.ceil(
                    high2.filter(
                      (d) =>
                        d[sceneProps[index]] !== null &&
                        d[sceneProps[index]] === 0
                    ).length / highColumns
                  ) *
                    (2 * R + countryMargin) -
                  (R + countryMargin)
                })`}
              >
                <text
                  className={s.noInfraLabel}
                  x={
                    R +
                    R +
                    highColumns * R * 2 -
                    (highColumns * 2 * R + 2 * R + 2 * R) / 2
                  }
                >
                  {countriesCapital}{" "}
                  <tspan style={{ fontWeight: "bold" }}>{without}</tspan> any{" "}
                  {labels[index]}
                </text>
              </g>
            </g>
          </g>
          {remark && (
            <text
              style={{ textAnchor: "middle", fontSize: 14 }}
              transform={`translate(${w / 2}, ${640})`}
            >
              {wordwrap(remark, Math.floor(w / 8)).map((d, i) => (
                <tspan
                  x={0}
                  y={0}
                  dy={
                    (wordwrap(remark, Math.floor(w / 8)).length < 3 ? 5 : 0) +
                    i *
                      (wordwrap(remark, Math.floor(w / 8)).length < 3 ? 16 : 14)
                  }
                  key={`remark-${i}`}
                >
                  {d}
                </tspan>
              ))}
            </text>
          )}

          <g transform={`translate(${w / 2}, 615)`}>
            <g>
              <text dy={-5} style={{ fontSize: 14, textAnchor: "middle" }}>
                {labels[index]}s per capita
              </text>
              <text
                x={-50}
                dx={-5}
                dy={5}
                style={{
                  fontSize: 14,
                  textAnchor: "end",
                  dominantBaseline: "central",
                }}
              >
                0
              </text>
              <text
                x={50}
                dx={5}
                dy={5}
                style={{
                  fontSize: 14,
                  textAnchor: "start",
                  dominantBaseline: "central",
                }}
              >
                >= 1
              </text>
              <rect x={-50} width={100} height={10} fill="url(#grad1)" />
            </g>
          </g>
        </g>
      </svg>
    </Vizualisation>
  );
};

export default Infrastructure;
